import { useEffect, useRef } from "react";
import { GoAway } from "../Icons/Interface";

interface GoAwayProps {
	onClick?: () => void;
	className?: string;
}

// Global LIFO stack to track all active GoAwayButton instances
// The LAST button added to the stack has priority for ESC handling
const goAwayStack: Array<{ id: number; onClick: () => void }> = [];
let nextId = 0;

// Single global ESC key handler - only the top of stack handles it
const handleGlobalEscape = (e: KeyboardEvent) => {
	if (e.key === "Escape" && goAwayStack.length > 0) {
		const topOfStack = goAwayStack[goAwayStack.length - 1];
		// console.log(
		// 	`Global ESC handler: invoking GoAwayButton [${topOfStack.id}] (top of stack)`
		// );
		// console.log(
		// 	`Stack state: [${goAwayStack.map((item) => item.id).join(", ")}]`
		// );
		topOfStack.onClick();
	}
};

// Register the global listener once at module load
window.addEventListener("keydown", handleGlobalEscape);
// console.log("GoAwayButton: Global ESC listener initialized");

/**
 * Triggers the onClick of the topmost GoAwayButton in the stack.
 * Use this for scrim clicks, programmatic closes, etc. that should
 * respect the LIFO stack order.
 * @returns true if a handler was invoked, false if stack is empty
 */
export const triggerTopGoAwayButton = (): boolean => {
	if (goAwayStack.length > 0) {
		const topOfStack = goAwayStack[goAwayStack.length - 1];
		// console.log(
		// 	`triggerTopGoAwayButton: invoking GoAwayButton [${topOfStack.id}] (top of stack)`
		// );
		// console.log(
		// 	`Stack state: [${goAwayStack.map((item) => item.id).join(", ")}]`
		// );
		topOfStack.onClick();
		return true;
	}
	// console.log("triggerTopGoAwayButton: stack is empty, no action taken");
	return false;
};

const GoAwayButton: React.FC<GoAwayProps> = ({
	onClick = () => {},
	className = "stroke-primary fill-primary hover:stroke-primary-inactive hover:fill-primary-inactive"
}) => {
	// Unique ID for this instance to track it in the stack
	const instanceId = useRef<number>(nextId++);

	// Register this instance in the LIFO stack on mount ONLY
	useEffect(() => {
		const id = instanceId.current; // Capture ID for use in cleanup
		const instance = { id, onClick };
		goAwayStack.push(instance);

		// console.log(
		// 	`GoAwayButton [${id}] registered. Stack depth: ${goAwayStack.length}`
		// );
		// console.log(
		// 	`Current stack order (bottom to top): [${goAwayStack.map((item) => item.id).join(", ")}]`
		// );

		// Cleanup function to remove this instance from the stack
		return () => {
			const index = goAwayStack.findIndex((item) => item.id === id);
			if (index !== -1) {
				goAwayStack.splice(index, 1);
				// console.log(
				// 	`GoAwayButton [${id}] unregistered. Stack depth: ${goAwayStack.length}`
				// );
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only register on mount, unregister on unmount

	// Update the onClick handler in the stack when it changes
	useEffect(() => {
		const instance = goAwayStack.find((item) => item.id === instanceId.current);
		if (instance) {
			instance.onClick = onClick;
			// console.log(
			// 	`GoAwayButton [${instanceId.current}] onClick handler updated`
			// );
		}
	}, [onClick]);

	const handleClick = () => {
		// Only handle click if this instance is at the top of the stack
		const topOfStack = goAwayStack[goAwayStack.length - 1];

		if (topOfStack && topOfStack.id === instanceId.current) {
			// console.log(
			// 	`GoAwayButton [${instanceId.current}] handling click (top of stack)`
			// );
			onClick();
		} else {
			// console.log(
			// 	`GoAwayButton [${instanceId.current}] ignoring click (not top of stack). Top is [${topOfStack?.id}]`
			// );
		}
	};

	return (
		<div onClick={handleClick} className={`cursor-pointer ${className} `}>
			{GoAway()}
		</div>
	);
};

export default GoAwayButton;
